import { navigateTo } from '../../../../../../common/frontend/route-utils.js'
import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { formatFte, humanReadableHours } from '../../../../../../common/utils/format-utils.js'
import type { TeamNormPayload } from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import './team-norm-card.css'

/**
 * Each team member's work-time norm fulfilment for the current month: a
 * progress bar of tracked vs expected-to-date hours plus their workload (FTE).
 * Rows open the person's profile "Tööaeg" tab for the detailed view.
 */
export const TeamNormCard = ({ data, lang }: { data: TeamNormPayload; lang: Language }) => {
    const c = t.portal.dashboard.cards.team_norm

    if (data.status === 'unavailable') {
        return <span className="portal-card__empty">{c.unavailable(lang)}</span>
    }

    if (data.status === 'no_team') {
        return <span className="portal-card__empty">{c.noTeam(lang)}</span>
    }

    if (data.members.length === 0) {
        return <span className="portal-card__empty">{c.empty(lang)}</span>
    }

    return (
        <div className="portal-team-norm">
            {data.members.map((m) => {
                // Zero expected hours (Harvest capacity 0) counts as met — the
                // person owes nothing, whatever they track is surplus.
                const percent =
                    m.expectedToDateHours > 0 ? (m.trackedHours / m.expectedToDateHours) * 100 : 100
                const behind = m.status === 'ok' && percent < 100
                return (
                    <button
                        key={m.employeeId}
                        type="button"
                        className="portal-team-norm__row"
                        onClick={() => navigateTo(`/people/${m.employeeId}?tab=worktime`)}
                    >
                        <div className="portal-team-norm__row-top">
                            <span className="portal-team-norm__name">
                                {m.firstName} {m.lastName}
                            </span>
                            {m.fte !== null && (
                                <span className="portal-team-norm__fte">
                                    {c.fte(formatFte(m.fte, lang))(lang)}
                                </span>
                            )}
                            <span className="portal-team-norm__hours">
                                {m.status === 'ok'
                                    ? c.hoursOfExpected(
                                          humanReadableHours(m.trackedHours),
                                          humanReadableHours(m.expectedToDateHours),
                                      )(lang)
                                    : c.notConnected(lang)}
                            </span>
                        </div>
                        {m.status === 'ok' && (
                            <div className="portal-team-norm__bar">
                                <div
                                    className={`portal-team-norm__bar-fill${
                                        behind ? ' portal-team-norm__bar-fill--behind' : ''
                                    }`}
                                    style={{ width: `${Math.min(100, percent)}%` }}
                                />
                            </div>
                        )}
                    </button>
                )
            })}
            {data.truncatedCount > 0 && (
                <span className="portal-card__subtext">
                    {c.truncated(data.truncatedCount)(lang)}
                </span>
            )}
        </div>
    )
}
